import {
  IPayerAutoSelectionRule,
  PayerAutoSelectionPayerType,
  PayerAutoSelectionRuleKind,
  PayerAutoSelectionSourceScope,
} from "types/PayerAutoSelection/PayerAutoSelection";

/**
 * CLE-2845 — the words on Eligibility › Payer Auto-Selection, in one place so the list, the dialog
 * and the tester say the same thing.
 */

/** Each kind's name, as the list, the dialog and the tester say it. The API's trace uses the same. */
export const KIND_LABELS: Record<PayerAutoSelectionRuleKind, string> = {
  MEDICAID_DUAL: "Medicaid / dual",
  MEDICARE_ADVANTAGE: "Medicare Advantage",
  PRODUCT_KEYWORD: "Product keyword",
  OUT_OF_AREA_DEFAULT: "Out-of-area default",
};

/** The intro under the buttons. */
export const INTRO =
  "When an eligibility check comes back, these rules decide whether the estimate's payer should change to what the response says the patient really has. Rules run top to bottom and the first one that fires wins; drag a row to change the order. A switch only goes to a payer the facility has set up, and staff see it on the estimate and can revert it.";

/** Where a Medicare Advantage rule switches to, in place of a target payer. */
export const MEDICARE_ADVANTAGE_TARGET =
  "The carrier's Medicare Advantage payer";

/** What a Medicare Advantage rule does, in the dialog under the kind. */
/** One short paragraph per kind, shown under the "What kind of rule" dropdown. */
export const KIND_EXPLANATIONS: Record<PayerAutoSelectionRuleKind, string> = {
  MEDICAID_DUAL:
    'For patients on Medicaid or a Medicare-Medicaid dual plan, who owe $0. When a keyword is in the plan name (or PolicyType says Medicaid), the estimate switches to the Medicaid payer chosen below. Keep these rules above Medicare Advantage so a "Medicare dual" plan lands here.',
  MEDICARE_ADVANTAGE:
    "For a patient on a Medicare Advantage plan while a Commercial payer is selected. When a keyword is in the plan name, the estimate switches to that carrier's Medicare Advantage payer at this facility (the one carrying the selected payer's parent; a payer with no parent has none). Keep Medicaid / dual rules above it.",
  PRODUCT_KEYWORD:
    "For carriers with several products on one pVerify code, like Network S and Network P. When a keyword is in the plan name, the estimate switches to the product payer chosen below. Order matters: put the more specific product (Blue Select Plus) above the general one (Blue Select).",
  OUT_OF_AREA_DEFAULT:
    "For Blue Cross members from another state whose plan name names no product this facility has. If no rule above matched and the response shows an out-of-state Blue member (a 3-digit PlanNumber, or another state's Blue plan answering) on a Commercial payer, the estimate switches to the payer chosen below.",
};

export const MEDICARE_ADVANTAGE_EXPLANATION =
  KIND_EXPLANATIONS.MEDICARE_ADVANTAGE;

/**
 * The words a NEW Medicare Advantage rule starts with — the seeded rule's. "MEDICARE SUPPLEMENT", not
 * "SUPPLEMENT": a Medicare Advantage plan can advertise "W/ SUPPLEMENTAL BENEFITS".
 */
export const MEDICARE_ADVANTAGE_DEFAULTS: {
  keywords: string[];
  excludeKeywords: string[];
  sourcePayerTypes: PayerAutoSelectionPayerType[];
  /** The twin often carries another pVerify code, so a Medicare Advantage rule switches from any payer. */
  sourceScope: PayerAutoSelectionSourceScope;
} = {
  keywords: ["MEDICARE", "MED ADV", "PART C", "SPECIAL NEEDS"],
  excludeKeywords: [
    "WITHOUT MEDICARE",
    "NON MEDICARE",
    "NO MEDICARE",
    "W/O MEDICARE",
    "MEDICARE SUPPLEMENT",
    "MEDIGAP",
  ],
  sourcePayerTypes: ["Commercial"],
  sourceScope: "ANY_PAYER",
};

export const PAYER_TYPE_LABELS: Record<PayerAutoSelectionPayerType, string> = {
  Commercial: "Commercial",
  UhcMedicareAdvantage: "Medicare Advantage",
  OriginalMedicare: "Medicare",
  Medicaid: "Medicaid",
};

export const PAYER_TYPE_CHOICES = (
  Object.keys(PAYER_TYPE_LABELS) as PayerAutoSelectionPayerType[]
).map((value) => ({ label: PAYER_TYPE_LABELS[value], value }));

/** What an out-of-area row matches, in words. */
export const OUT_OF_AREA_CONDITION =
  "Out-of-state Blue member (3-digit PlanNumber, or another Blue plan answering) on a Commercial payer";

export const KIND_CHOICES: Array<{
  label: string;
  value: PayerAutoSelectionRuleKind;
}> = [
  {
    label: "Medicaid / dual plan → the facility's Medicaid payer",
    value: "MEDICAID_DUAL",
  },
  {
    label: "Medicare Advantage plan → the carrier's Medicare Advantage payer",
    value: "MEDICARE_ADVANTAGE",
  },
  {
    label: "Plan name names a product → that product's payer",
    value: "PRODUCT_KEYWORD",
  },
  {
    label: "Out-of-state Blue member → default payer",
    value: "OUT_OF_AREA_DEFAULT",
  },
];

/**
 * The one "Switch from" control in the dialog. Three of its choices are the stored `sourceScope`; the fourth,
 * PAYER_TYPES, is stored as `sourceScope = ANY_PAYER` plus a non-empty `sourcePayerTypes` (the Medicare Advantage
 * row: any carrier, Commercial selections only).
 */
export type SourceMode = PayerAutoSelectionSourceScope | "PAYER_TYPES";

export const SOURCE_CHOICES: Array<{ label: string; value: SourceMode }> = [
  {
    label: "Any payer with the same pVerify code (default)",
    value: "SAME_PVERIFY_CODE",
  },
  { label: "Any selected payer, any carrier", value: "ANY_PAYER" },
  { label: "Any selected payer of these types…", value: "PAYER_TYPES" },
  { label: "Only these selected payers…", value: "LISTED_PAYERS" },
];

export const sourceModeOf = (
  sourceScope: PayerAutoSelectionSourceScope,
  sourcePayerTypes: PayerAutoSelectionPayerType[]
): SourceMode =>
  sourcePayerTypes.length > 0 && sourceScope === "ANY_PAYER"
    ? "PAYER_TYPES"
    : sourceScope;

/** Under both keyword fields in the dialog. Matching is by substring. */
export const KEYWORD_CAPTION =
  'Matches anywhere in the plan name: "AETNA" also matches "AETNAPLUS". At least 3 characters. Type a word or phrase and press Enter.';

/** What the dialog and the API say about a keyword under 3 characters. */
export const SHORT_KEYWORD_PROBLEM = "Keywords need at least 3 characters.";

/** The shortest keyword the API accepts, after normalisation (matching is by substring). */
export const MIN_KEYWORD_LENGTH = 3;

export const OPTION_TEXT = {
  wholeSegment: {
    label: "Single-word keywords must be the entire segment",
    help: 'For common words like "BASIC" and "STANDARD": "BASIC" matches a plan name segment that is exactly BASIC, but not "UHC BRONZE STANDARD PLAN". Multi-word keywords still match normally.',
  },
  skipOutOfState: {
    label: "Ignore members of another state's Blue plan",
    help: "pVerify marks those with a 3-digit PlanNumber. Turn this on when another state's plan uses the same product name — Georgia's \"BLUE CHOICE\" is not Oklahoma's Blue Choice.",
  },
  matchPolicyTypeMedicaid: {
    label: "Also match when pVerify's PolicyType says Medicaid",
    help: "Catches dual plans whose name does not say so.",
  },
};

export const accountsInWords = (rule: IPayerAutoSelectionRule): string =>
  rule.accountIds.length === 0
    ? "All facilities"
    : rule.accounts.map((account) => account.name).join(", ") ||
      `${rule.accountIds.length} facilities`;

export const sourceInWords = (rule: IPayerAutoSelectionRule): string => {
  if (rule.sourceScope === "ANY_PAYER") return "from any selected payer";
  if (rule.sourceScope === "LISTED_PAYERS") {
    return `from ${
      rule.sourcePayers.map((payer) => payer.name).join(", ") ||
      "the listed payers"
    }`;
  }
  return "from any payer with the same pVerify code";
};

/** "only from Commercial payers", or null when the rule switches from any type. */
export const payerTypesInWords = (
  rule: IPayerAutoSelectionRule
): string | null =>
  (rule.sourcePayerTypes ?? []).length === 0
    ? null
    : `only from ${rule.sourcePayerTypes
        .map((type) => PAYER_TYPE_LABELS[type] ?? type)
        .join(" or ")} payers`;

export const optionsInWords = (rule: IPayerAutoSelectionRule): string[] =>
  [
    rule.wholeSegment && "single words must be the entire segment",
    rule.skipOutOfState && "ignores other states' Blue members",
  ].filter((option): option is string => !!option);

/** The rule kind behind each recommendation reason, for the tester's result line. */
export const REASON_LABELS: Record<string, string> = {
  MEDICAID_DUAL: KIND_LABELS.MEDICAID_DUAL,
  MEDICARE_ADVANTAGE: KIND_LABELS.MEDICARE_ADVANTAGE,
  PLAN_NAME: KIND_LABELS.PRODUCT_KEYWORD,
  OUT_OF_AREA_DEFAULT: KIND_LABELS.OUT_OF_AREA_DEFAULT,
};
